(function () {
  'use strict';

  var SELECTOR = '[data-mu-interactive-mode="true"]';
  var MAX_SPLATS = 6;
  var TILT_LIMIT = 0.55;
  var FRAME_INTERVAL = 1000 / 30;
  var PARALLAX_SCALE = 1.035;
  var PARALLAX_X = 1.25;
  var PARALLAX_Y = 0.75;
  var instances = new WeakMap();
  var webglSupported;

  function canUseInteractiveMode() {
    if (!window.matchMedia) return false;
    if (!window.matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)').matches) return false;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    if (webglSupported === undefined) webglSupported = testWebGL();
    return webglSupported;
  }

  function testWebGL() {
    try {
      var canvas = document.createElement('canvas');
      var gl = canvas.getContext('webgl', { antialias: false, alpha: true });
      return !!gl;
    } catch (error) {
      return false;
    }
  }

  function strengthValue(value) {
    if (value === 'strong') return 1.55;
    if (value === 'medium') return 1.15;
    return 0.78;
  }

  function getSourceUrl(el) {
    var explicit = el.getAttribute('data-mu-interactive-src');
    if (explicit) return explicit;

    var img = getDesktopImage(el);
    if (!img) return '';

    var src = img.currentSrc || img.getAttribute('src') || img.getAttribute('data-src') || '';
    if (src.indexOf('data:image') === 0) src = img.getAttribute('data-src') || '';
    return src.replace(/([?&]width=)1(&|$)/, '$12400$2');
  }

  function getDesktopImage(el) {
    return el.querySelector('img.t4s-d-none.t4s-d-md-block') || el.querySelector('img.t4s-img-as-bg');
  }

  function isGifUrl(value) {
    return /\.gif(?:$|[?#])/i.test(String(value || ''));
  }

  function getImageSource(img) {
    if (!img) return '';
    var src = img.currentSrc || img.getAttribute('src') || img.getAttribute('data-src') || '';
    if (src.indexOf('data:image') === 0) src = img.getAttribute('data-src') || '';
    return src;
  }

  function parsePosition(value) {
    var parts = String(value || '50% 50%').trim().split(/\s+/);

    function toRatio(part, axis) {
      if (!part) return 0.5;
      if (part === 'left' || part === 'top') return 0;
      if (part === 'right' || part === 'bottom') return 1;
      if (part === 'center') return 0.5;
      if (part.indexOf('%') > -1) return Math.max(0, Math.min(1, parseFloat(part) / 100));
      if (part.indexOf('px') > -1) return axis ? Math.max(0, parseFloat(part)) : 0.5;
      return 0.5;
    }

    return {
      x: toRatio(parts[0], true),
      y: toRatio(parts[1], false)
    };
  }

  function compileShader(gl, type, source) {
    var shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  function createProgram(gl) {
    var vertex = compileShader(gl, gl.VERTEX_SHADER, [
      'attribute vec2 a_position;',
      'varying vec2 v_uv;',
      'void main(){',
      '  v_uv = (a_position + 1.0) * 0.5;',
      '  gl_Position = vec4(a_position, 0.0, 1.0);',
      '}'
    ].join('\n'));

    var fragment = compileShader(gl, gl.FRAGMENT_SHADER, [
      'precision mediump float;',
      'uniform sampler2D u_texture;',
      'uniform vec4 u_cover;',
      'uniform float u_aspect;',
      'uniform vec2 u_mouse;',
      'uniform vec2 u_velocity;',
      'uniform vec2 u_tilt;',
      'uniform vec4 u_splats[6];',
      'uniform vec2 u_splat_velocity[6];',
      'uniform float u_splat_life[6];',
      'uniform float u_strength;',
      'uniform float u_active;',
      'uniform float u_overlay_mode;',
      'varying vec2 v_uv;',
      'vec2 coverUv(vec2 uv){',
      '  return clamp((uv - u_cover.xy) / u_cover.zw, 0.0, 1.0);',
      '}',
      'void main(){',
      '  vec2 plane = v_uv - 0.5;',
      '  vec2 planeUv = plane + 0.5 + u_tilt * 0.002 * u_strength;',
      '  vec2 fluid = vec2(0.0);',
      '  float field = 0.0;',
      '  for (int i = 0; i < 6; i++){',
      '    float life = u_splat_life[i];',
      '    vec2 point = u_splats[i].xy;',
      '    vec2 prevPoint = u_splats[i].zw;',
      '    vec2 velocity = u_splat_velocity[i];',
      '    vec2 segment = point - prevPoint;',
      '    vec2 segmentScaled = vec2(segment.x * u_aspect, segment.y);',
      '    vec2 toPoint = planeUv - prevPoint;',
      '    vec2 toPointScaled = vec2(toPoint.x * u_aspect, toPoint.y);',
      '    float segmentLength = max(dot(segmentScaled, segmentScaled), 0.00001);',
      '    float h = clamp(dot(toPointScaled, segmentScaled) / segmentLength, 0.0, 1.0);',
      '    vec2 nearest = prevPoint + segment * h;',
      '    vec2 delta = planeUv - nearest;',
      '    vec2 scaled = vec2(delta.x * u_aspect, delta.y);',
      '    float d2 = dot(scaled, scaled);',
      '    float core = exp(-d2 / 0.016) * life;',
      '    float wake = exp(-d2 / 0.055) * life;',
      '    fluid += (-velocity * core * 0.035 + normalize(delta + vec2(0.0001)) * wake * 0.0025) * u_strength;',
      '    field += core;',
      '  }',
      '  vec2 mouseDelta = planeUv - u_mouse;',
      '  vec2 mouseScaled = vec2(mouseDelta.x * u_aspect, mouseDelta.y);',
      '  float mouseField = exp(-dot(mouseScaled, mouseScaled) / 0.018) * u_active;',
      '  vec2 cursorPush = (-u_velocity * 0.85 + normalize(mouseDelta + vec2(0.0001)) * 0.004) * mouseField * u_strength;',
      '  vec2 displaced = planeUv + fluid + cursorPush;',
      '  vec4 base = texture2D(u_texture, coverUv(displaced));',
      '  vec4 softened = texture2D(u_texture, coverUv(displaced + (fluid + cursorPush) * 0.35));',
      '  float mixAmount = clamp((field + mouseField) * 0.12, 0.0, 0.22);',
      '  vec4 color = mix(base, softened, mixAmount);',
      '  float angleShade = 1.0 + dot(plane, vec2(u_tilt.x, -u_tilt.y)) * 0.055 * u_strength;',
      '  float effectMask = clamp((field + mouseField) * 0.62, 0.0, 0.72);',
      '  float alpha = mix(color.a, effectMask, u_overlay_mode);',
      '  gl_FragColor = vec4(color.rgb * angleShade, alpha);',
      '}'
    ].join('\n'));

    if (!vertex || !fragment) return null;

    var program = gl.createProgram();
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    gl.deleteShader(vertex);
    gl.deleteShader(fragment);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      gl.deleteProgram(program);
      return null;
    }

    return program;
  }

  function InteractiveSlide(el) {
    this.el = el;
    this.canvas = el.querySelector('[data-mu-interactive-canvas]');
    this.item = el.closest('.t4s-slideshow-item');
    this.strength = strengthValue(el.getAttribute('data-mu-interactive-strength'));
    this.gl = null;
    this.program = null;
    this.texture = null;
    this.image = null;
    this.hiddenAnimatedImage = null;
    this.animatedTexture = false;
    this.ready = false;
    this.running = false;
    this.frame = 0;
    this.startTime = 0;
    this.lastFrameTime = 0;
    this.lastDrawTime = 0;
    this.pointer = { x: 0.5, y: 0.5, tx: 0.5, ty: 0.5, vx: 0, vy: 0, active: 0, targetActive: 0 };
    this.cover = { x: 0, y: 0, w: 1, h: 1 };
    this.aspect = 1;
    this.splats = [];
    this.splatData = new Float32Array(MAX_SPLATS * 4);
    this.splatVelocity = new Float32Array(MAX_SPLATS * 2);
    this.splatLife = new Float32Array(MAX_SPLATS);
    this.lastPointerMoveTime = 0;
    this.boundRender = this.render.bind(this);
    this.boundResize = this.resize.bind(this);
    this.boundPointerMove = this.onPointerMove.bind(this);
    this.boundPointerLeave = this.onPointerLeave.bind(this);
    this.observer = null;
  }

  InteractiveSlide.prototype.init = function () {
    if (!this.canvas || !canUseInteractiveMode()) return;
    if (!this.setupGL()) return;

    this.el.addEventListener('pointermove', this.boundPointerMove, { passive: true });
    this.el.addEventListener('pointerleave', this.boundPointerLeave);
    window.addEventListener('resize', this.boundResize);

    if (window.MutationObserver && this.item) {
      this.observer = new MutationObserver(this.updateActiveState.bind(this));
      this.observer.observe(this.item, { attributes: true, attributeFilter: ['class'] });
    }

    this.loadTexture();
  };

  InteractiveSlide.prototype.setupGL = function () {
    var gl = this.canvas.getContext('webgl', {
      antialias: false,
      alpha: true,
      premultipliedAlpha: false,
      preserveDrawingBuffer: false
    });
    if (!gl) return false;

    var program = createProgram(gl);
    if (!program) return false;

    var buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1, -1,
      1, -1,
      -1, 1,
      -1, 1,
      1, -1,
      1, 1
    ]), gl.STATIC_DRAW);

    this.gl = gl;
    this.program = program;
    this.buffer = buffer;
    this.locations = {
      position: gl.getAttribLocation(program, 'a_position'),
      texture: gl.getUniformLocation(program, 'u_texture'),
      cover: gl.getUniformLocation(program, 'u_cover'),
      aspect: gl.getUniformLocation(program, 'u_aspect'),
      mouse: gl.getUniformLocation(program, 'u_mouse'),
      velocity: gl.getUniformLocation(program, 'u_velocity'),
      tilt: gl.getUniformLocation(program, 'u_tilt'),
      splats: gl.getUniformLocation(program, 'u_splats[0]'),
      splatVelocity: gl.getUniformLocation(program, 'u_splat_velocity[0]'),
      splatLife: gl.getUniformLocation(program, 'u_splat_life[0]'),
      strength: gl.getUniformLocation(program, 'u_strength'),
      active: gl.getUniformLocation(program, 'u_active'),
      overlayMode: gl.getUniformLocation(program, 'u_overlay_mode')
    };

    return true;
  };

  InteractiveSlide.prototype.loadTexture = function () {
    var src = getSourceUrl(this.el);
    if (!src) return;

    var originalSrc = this.el.getAttribute('data-mu-interactive-original-src') || '';
    var forceAnimated = this.el.getAttribute('data-mu-interactive-animated') === 'true';
    var desktopImage = getDesktopImage(this.el);
    var desktopSrc = getImageSource(desktopImage);
    this.animatedTexture = forceAnimated || isGifUrl(originalSrc) || isGifUrl(src) || isGifUrl(desktopSrc);

    if (this.animatedTexture) {
      src = originalSrc || desktopSrc || src;
      this.el.classList.add('is-mu-interactive-animated');
    }

    var image = new Image();
    image.crossOrigin = 'anonymous';
    image.decoding = 'async';

    if (this.animatedTexture) {
      image.alt = '';
      image.setAttribute('aria-hidden', 'true');
      image.className = 't4s-not-style mu-interactive-animated-source';
      image.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;opacity:0;pointer-events:none;z-index:-1;';
      this.el.appendChild(image);
      this.hiddenAnimatedImage = image;
    }

    image.onload = function () {
      if (!this.gl) return;
      this.image = image;
      this.texture = this.gl.createTexture();
      this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture);
      this.gl.pixelStorei(this.gl.UNPACK_FLIP_Y_WEBGL, true);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR);
      this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, this.gl.RGBA, this.gl.UNSIGNED_BYTE, image);
      this.ready = true;
      this.resize();
      this.el.classList.add('is-mu-interactive-ready');
      this.updateActiveState();
    }.bind(this);
    image.onerror = this.destroy.bind(this);
    image.src = src;
  };

  InteractiveSlide.prototype.resize = function () {
    if (!this.gl || !this.image) return;

    var rect = this.el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var width = Math.max(1, Math.round(rect.width * dpr));
    var height = Math.max(1, Math.round(rect.height * dpr));

    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }

    this.gl.viewport(0, 0, width, height);
    this.aspect = rect.width / rect.height;

    var img = getDesktopImage(this.el);
    var position = img ? parsePosition(window.getComputedStyle(img).objectPosition) : { x: 0.5, y: 0.5 };
    var scale = Math.max(rect.width / this.image.naturalWidth, rect.height / this.image.naturalHeight);
    var displayWidth = this.image.naturalWidth * scale;
    var displayHeight = this.image.naturalHeight * scale;
    var coverWidth = displayWidth / rect.width;
    var coverHeight = displayHeight / rect.height;

    this.cover.x = (1 - coverWidth) * position.x;
    this.cover.y = (1 - coverHeight) * (1 - position.y);
    this.cover.w = coverWidth;
    this.cover.h = coverHeight;

    this.renderStatic();
  };

  InteractiveSlide.prototype.onPointerMove = function (event) {
    if (!this.ready) return;
    this.el.classList.add('is-mu-interactive-active');

    var rect = this.el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    var x = (event.clientX - rect.left) / rect.width;
    var y = 1 - ((event.clientY - rect.top) / rect.height);
    x = Math.max(0, Math.min(1, x));
    y = Math.max(0, Math.min(1, y));

    var now = performance.now();
    var hasRecentMove = this.lastPointerMoveTime && now - this.lastPointerMoveTime < 90;
    var prevX = hasRecentMove ? this.pointer.tx : x;
    var prevY = hasRecentMove ? this.pointer.ty : y;

    this.pointer.vx = x - prevX;
    this.pointer.vy = y - prevY;
    this.pointer.tx = x;
    this.pointer.ty = y;
    this.pointer.targetActive = 1;
    this.lastPointerMoveTime = now;
    this.addSplat(x, y, prevX, prevY, this.pointer.vx, this.pointer.vy);
    if (this.isSelected() && canUseInteractiveMode()) this.start();
  };

  InteractiveSlide.prototype.onPointerLeave = function () {
    this.pointer.targetActive = 0;
    if (this.isSelected() && canUseInteractiveMode()) this.start();
  };

  InteractiveSlide.prototype.isSelected = function () {
    if (!this.item || !this.item.parentElement) return true;
    if (this.item.classList.contains('is-selected')) return true;
    return !this.item.parentElement.querySelector('.t4s-slideshow-item.is-selected');
  };

  InteractiveSlide.prototype.updateActiveState = function () {
    if (!this.ready) return;
    if (this.isSelected() && canUseInteractiveMode()) {
      if (this.animatedTexture) {
        this.start();
      } else {
        this.renderStatic();
      }
    } else {
      this.stop();
    }
  };

  InteractiveSlide.prototype.start = function () {
    if (this.running) return;
    this.running = true;
    this.startTime = performance.now();
    this.lastFrameTime = 0;
    this.lastDrawTime = 0;
    this.frame = requestAnimationFrame(this.boundRender);
  };

  InteractiveSlide.prototype.stop = function () {
    this.running = false;
    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
    this.pointer.targetActive = 0;
    this.pointer.active = 0;
    this.pointer.vx = 0;
    this.pointer.vy = 0;
    this.splats = [];
    this.el.classList.remove('is-mu-interactive-active');
    this.resetPlaneTransform();
    this.renderStatic();
  };

  InteractiveSlide.prototype.renderStatic = function () {
    if (!this.ready || this.running) return;
    this.draw(0);
  };

  InteractiveSlide.prototype.render = function (time) {
    if (!this.running) return;
    if (!this.isSelected() || !canUseInteractiveMode()) {
      this.stop();
      return;
    }

    if (this.lastDrawTime && time - this.lastDrawTime < FRAME_INTERVAL) {
      this.frame = requestAnimationFrame(this.boundRender);
      return;
    }

    var p = this.pointer;
    var dt = this.lastFrameTime ? Math.min(0.05, Math.max(0, (time - this.lastFrameTime) / 1000)) : 1 / 60;
    this.lastFrameTime = time;
    this.lastDrawTime = time;
    p.x += (p.tx - p.x) * 0.16;
    p.y += (p.ty - p.y) * 0.16;
    p.vx *= 0.82;
    p.vy *= 0.82;
    p.active += (p.targetActive - p.active) * 0.12;
    this.updateSplats(dt);

    this.draw((time - this.startTime) / 1000);

    var isIdle = p.targetActive <= 0 && p.active < 0.01 && Math.abs(p.vx) < 0.0001 && Math.abs(p.vy) < 0.0001 && !this.hasLiveSplats();

    if (this.animatedTexture && isIdle) {
      this.el.classList.remove('is-mu-interactive-active');
    }

    if (!this.animatedTexture && isIdle) {
      p.active = 0;
      p.vx = 0;
      p.vy = 0;
      this.running = false;
      this.frame = 0;
      this.renderStatic();
      return;
    }

    this.frame = requestAnimationFrame(this.boundRender);
  };

  InteractiveSlide.prototype.draw = function (time) {
    var gl = this.gl;
    if (!gl || !this.texture) return;
    var tilt = this.getTilt();
    this.prepareSplatUniforms();

    gl.useProgram(this.program);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    gl.enableVertexAttribArray(this.locations.position);
    gl.vertexAttribPointer(this.locations.position, 2, gl.FLOAT, false, 0, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    if (this.animatedTexture && this.image && this.image.complete) {
      try {
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, this.image);
      } catch (error) {}
    }
    gl.uniform1i(this.locations.texture, 0);
    gl.uniform4f(this.locations.cover, this.cover.x, this.cover.y, this.cover.w, this.cover.h);
    gl.uniform1f(this.locations.aspect, this.aspect || 1);
    gl.uniform2f(this.locations.mouse, this.pointer.x, this.pointer.y);
    gl.uniform2f(this.locations.velocity, this.pointer.vx, this.pointer.vy);
    gl.uniform2f(this.locations.tilt, tilt.x, tilt.y);
    gl.uniform4fv(this.locations.splats, this.splatData);
    gl.uniform2fv(this.locations.splatVelocity, this.splatVelocity);
    gl.uniform1fv(this.locations.splatLife, this.splatLife);
    gl.uniform1f(this.locations.strength, this.strength);
    gl.uniform1f(this.locations.active, this.pointer.active);
    gl.uniform1f(this.locations.overlayMode, this.animatedTexture ? 1 : 0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    this.updatePlaneTransform(tilt);
  };

  InteractiveSlide.prototype.getTilt = function () {
    var p = this.pointer;
    var active = Math.max(0, Math.min(1, p.active));

    return {
      x: (p.x - 0.5) * 2 * active * TILT_LIMIT,
      y: (p.y - 0.5) * 2 * active * TILT_LIMIT
    };
  };

  InteractiveSlide.prototype.updatePlaneTransform = function (tilt) {
    var x = tilt ? tilt.x : 0;
    var y = tilt ? tilt.y : 0;
    var tx = (-x * PARALLAX_X).toFixed(3);
    var ty = (y * PARALLAX_Y).toFixed(3);
    var transform = 'translate3d(' + tx + '%, ' + ty + '%, 0) scale(' + PARALLAX_SCALE + ')';

    this.canvas.style.transform = transform;

    if (this.animatedTexture) {
      this.applyBaseImageTransform(transform);
    }
  };

  InteractiveSlide.prototype.applyBaseImageTransform = function (transform) {
    var image = getDesktopImage(this.el);
    if (image) image.style.transform = transform;
  };

  InteractiveSlide.prototype.resetPlaneTransform = function () {
    var transform = 'scale(' + PARALLAX_SCALE + ')';
    if (this.canvas) this.canvas.style.transform = transform;
    if (this.animatedTexture) {
      this.applyBaseImageTransform(transform);
    }
  };

  InteractiveSlide.prototype.addSplat = function (x, y, prevX, prevY, vx, vy) {
    var speed = Math.sqrt(vx * vx + vy * vy);
    if (speed < 0.0008) return;

    this.splats.unshift({
      x: x,
      y: y,
      prevX: prevX,
      prevY: prevY,
      vx: Math.max(-0.08, Math.min(0.08, vx * 8)),
      vy: Math.max(-0.08, Math.min(0.08, vy * 8)),
      life: Math.min(1, 0.45 + speed * 18)
    });

    if (this.splats.length > MAX_SPLATS) this.splats.length = MAX_SPLATS;
  };

  InteractiveSlide.prototype.updateSplats = function (dt) {
    var decay = Math.exp(-dt * 3.4);

    for (var i = this.splats.length - 1; i >= 0; i -= 1) {
      var splat = this.splats[i];
      splat.life *= decay;
      splat.vx *= 0.96;
      splat.vy *= 0.96;
      if (splat.life < 0.015) this.splats.splice(i, 1);
    }
  };

  InteractiveSlide.prototype.hasLiveSplats = function () {
    return this.splats.length > 0;
  };

  InteractiveSlide.prototype.prepareSplatUniforms = function () {
    this.splatData.fill(0);
    this.splatVelocity.fill(0);
    this.splatLife.fill(0);

    for (var i = 0; i < this.splats.length && i < MAX_SPLATS; i += 1) {
      var splat = this.splats[i];
      var offset = i * 4;
      var velocityOffset = i * 2;
      this.splatData[offset] = splat.x;
      this.splatData[offset + 1] = splat.y;
      this.splatData[offset + 2] = splat.prevX;
      this.splatData[offset + 3] = splat.prevY;
      this.splatVelocity[velocityOffset] = splat.vx;
      this.splatVelocity[velocityOffset + 1] = splat.vy;
      this.splatLife[i] = splat.life;
    }
  };

  InteractiveSlide.prototype.destroy = function () {
    this.stop();
    this.el.classList.remove('is-mu-interactive-ready');
    this.el.classList.remove('is-mu-interactive-animated');
    this.el.classList.remove('is-mu-interactive-active');
    if (this.canvas) this.canvas.style.transform = '';
    var image = getDesktopImage(this.el);
    if (image) image.style.transform = '';
    if (this.hiddenAnimatedImage && this.hiddenAnimatedImage.parentNode) {
      this.hiddenAnimatedImage.parentNode.removeChild(this.hiddenAnimatedImage);
    }
    this.el.removeEventListener('pointermove', this.boundPointerMove);
    this.el.removeEventListener('pointerleave', this.boundPointerLeave);
    window.removeEventListener('resize', this.boundResize);
    if (this.observer) this.observer.disconnect();
    if (this.gl) {
      if (this.texture) this.gl.deleteTexture(this.texture);
      if (this.buffer) this.gl.deleteBuffer(this.buffer);
      if (this.program) this.gl.deleteProgram(this.program);
    }
    this.gl = null;
  };

  function initInteractiveSlides(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll ? scope.querySelectorAll(SELECTOR) : [];

    for (var i = 0; i < nodes.length; i += 1) {
      var node = nodes[i];
      var instance = instances.get(node);

      if (!canUseInteractiveMode()) {
        if (instance) {
          instance.destroy();
          instances.delete(node);
        }
        continue;
      }

      if (!instance) {
        instance = new InteractiveSlide(node);
        instances.set(node, instance);
        instance.init();
      } else {
        instance.updateActiveState();
      }
    }
  }

  function destroyInteractiveSlides(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll ? scope.querySelectorAll(SELECTOR) : [];

    for (var i = 0; i < nodes.length; i += 1) {
      var node = nodes[i];
      var instance = instances.get(node);

      if (instance) {
        instance.destroy();
        instances.delete(node);
      }
    }
  }

  function boot() {
    initInteractiveSlides(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('load', boot);
  window.addEventListener('resize', function () {
    initInteractiveSlides(document);
  });
  document.addEventListener('shopify:section:load', function (event) {
    initInteractiveSlides(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    destroyInteractiveSlides(event.target);
  });
}());
